import React, {
	memo,
	useCallback,
	useMemo
} from 'react';
import PropTypes from 'prop-types';
import { log } from '../../services/utils';
import { Dropdown } from 'semantic-ui-react';
import FormInput from './FormInput';

const FormTimeInput = ( {
	align = 'left',
	defaultAmOrPm = 'AM',
	errors = [],
	fieldLabel,
	fieldLabelAlignment = 'left',
	fieldLabelPosition = 'above',
	forceValidation,
	formatter,
	helpId,
	icon,
	instructions,
	name,
	onBlur,
	onChange,
	onFocus,
	readOnly,
	required,
	spellcheck = true,
	type = 'text',
	value,
	width,
	...props
} ) => {
	const amOrPm = useMemo( () => value?.split( ' ' )[ 1 ] ?? defaultAmOrPm, [ defaultAmOrPm, value ] );

	const handleBlur = useCallback( ( { target } ) => {
		const { name, value } = target;

		if ( value ) {
			// remove all non-digits (or colons) and split string into an array
			const str = value.replace( /[^0-9:]/g, '' );

			let updatedValue = str;

			switch ( str.length ) {
				case 1:
					updatedValue = `0${ str }:00  ${ amOrPm }`;
					break;
				case 2:
					updatedValue = `${ str }:00 ${ amOrPm }`;
					break;
				case 3:
					updatedValue = `${ str }00 ${ amOrPm }`;
					break;
				case 4:
					updatedValue = `${ str }0 ${ amOrPm }`;
					break;
				default:
					updatedValue = `${ str } ${ amOrPm }`;
					break;
			}

			onChange( {
				target: {
					name,
					value: updatedValue
				}
			} );
		}

		onBlur?.( target );
	}, [
		amOrPm,
		onChange,
		onBlur
	] );

	const options = useMemo( () => [
		{ key: 'PM', text: 'PM', value: 'PM' },
		{ key: 'AM', text: 'AM', value: 'AM' }
	], [] );

	const inputLabel = useMemo( () => (
		<Dropdown
			compact
			labeled
			options={options}
			onChange={ ( e, { value: amOrPmValue } ) => {
				const str = value.replace( /[^0-9:]/g, '' );

				onChange( {
					target: {
						name,
						value: `${ str } ${ amOrPmValue }`
					}
				} );
			}}
			selection
			value={ amOrPm }
		/>
	), [
		amOrPm,
		name,
		onChange,
		options,
		value
	] );

	return (
		<>
			{ log( `- ${ name } input` ) }
			{ console.log( value ) }
			{ console.log( value.split( ' ' )[ 1 ] ) }
			<FormInput
				align={align}
				errors={errors}
				fieldLabel={fieldLabel}
				fieldLabelAlignment={fieldLabelAlignment}
				fieldLabelPosition={fieldLabelPosition}
				forceValidation={forceValidation}
				formatter={formatter}
				helpId={helpId}
				icon={icon}
				inputLabel={inputLabel}
				inputLabelPosition="right"
				instructions={instructions}
				keyDown={null}
				mask="time"
				name={name}
				onBlur={handleBlur}
				onChange={onChange}
				onFocus={onFocus}
				readOnly={readOnly}
				required={required}
				spellcheck={spellcheck}
				type={type}
				value={value}
				width={width}
				{...props}
			/>
		</>
	);
};

FormTimeInput.propTypes = {
	align: PropTypes.string,
	defaultAmOrPm: PropTypes.oneOf( [ 'AM', 'PM' ] ),
	errors: PropTypes.arrayOf( PropTypes.shape( {
		error: PropTypes.bool,
		message: PropTypes.node
	} ) ),
	fieldLabel: PropTypes.node,
	fieldLabelAlignment: PropTypes.string,
	fieldLabelPosition: PropTypes.string,
	forceValidation: PropTypes.bool,
	formatter: PropTypes.string,
	helpId: PropTypes.number,
	icon: PropTypes.node,
	inputLabel: PropTypes.node,
	inputLabelPosition: PropTypes.oneOf( [
		'left',
		'right',
		'right corner',
		'left corner'
	] ),
	instructions: PropTypes.string,
	keyDown: PropTypes.oneOf( [
		'float',
		'integer'
	] ),
	mask: PropTypes.string,
	name: PropTypes.string.isRequired,
	onBlur: PropTypes.func,
	onChange: PropTypes.func,
	onFocus: PropTypes.func,
	readOnly: PropTypes.bool,
	required: PropTypes.bool,
	spellcheck: PropTypes.bool,
	type: PropTypes.oneOf( [
		'text',
		'number'
	] ),
	value: PropTypes.string,
	width: PropTypes.number
};

export default memo( FormTimeInput );
